import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { getSkuById } from './api-service';
import { DeviceProfiler, Viewer } from './gaussian-splats-3d.module'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import * as THREE from 'three'

const View3DNew = () => {
    const query = new URLSearchParams(useLocation().search);
    const sku_id = query.get('sku_id');
    const autoRotate = query.get('autoRotate');
    const performanceTier = query.get('tier'); // Add tier query parameter support
    const glbUrl = query.get('glbUrl'); // Optional GLB model URL
    const bg = query.get('bg'); // Background GLB model toggle
    const gradient = query.get('gradient'); // Gradient background toggle
    const viewerRef = useRef(null);
    const glbModelRef = useRef(null);
    const lightsRef = useRef([]);
    const fileInputRef = useRef(null);
    const [customModelUrl, setCustomModelUrl] = useState(null);

    if (!sku_id) {
        const errorMessage = 'Error: sku_id is required in URL parameters';
        console.error(errorMessage);
        document.body.innerHTML = `<div style="color: white; text-align: center; margin-top: 50px;">${errorMessage}</div>`;
        throw new Error(errorMessage);
    }

    useEffect(() => {
        const cleanupGLBModel = () => {
            if (glbModelRef.current && viewerRef.current?.threeScene) {
                viewerRef.current.threeScene.remove(glbModelRef.current);
                
                // Dispose of model resources
                glbModelRef.current.traverse((child) => {
                    if (child.geometry) {
                        child.geometry.dispose();
                    }
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(material => material.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                });
                
                glbModelRef.current = null;
                console.log('GLB model cleaned up successfully');
            }
        };

        const loadGLBModel = async (viewer, urlOrFile) => {
            return new Promise((resolve, reject) => {
                const loader = new GLTFLoader();
                
                // Convert File to URL if it's a File object
                let loadUrl = urlOrFile;
                if (urlOrFile instanceof File) {
                    loadUrl = URL.createObjectURL(urlOrFile);
                }
                
                loader.load(
                    loadUrl,
                    (gltf) => {
                        const model = gltf.scene;

                        model.rotation.x = Math.PI / 2;
                        model.position.z -= 1.0; 
                        
                        // Store reference to the model
                        glbModelRef.current = model;
                        
                        // Add model to the viewer's scene
                        viewer.threeScene.add(model);
                        
                        console.log('GLB model loaded successfully:', urlOrFile instanceof File ? urlOrFile.name : urlOrFile);
                        
                        // Clean up object URL if it was created from a File
                        if (urlOrFile instanceof File) {
                            URL.revokeObjectURL(loadUrl);
                        }
                        
                        resolve(model);
                    },
                    (progress) => {
                        // Progress callback
                        const percentComplete = (progress.loaded / progress.total) * 100;
                        console.log(`GLB loading progress: ${percentComplete.toFixed(2)}%`);
                    },
                    (error) => {
                        console.error('Error loading GLB model:', error);
                        // Clean up object URL if it was created from a File
                        if (urlOrFile instanceof File) {
                            URL.revokeObjectURL(loadUrl);
                        }
                        reject(error);
                    }
                );
            });
        };

        const initViewer = async () => {
            try {
                const skuData = await getSkuById(sku_id);
                
                // Form the URL with the sku_id (Not taking this from env coz of 360 bucket conflict)
                // const pointCloudUrl = `https://d2b9isnedwxntv.cloudfront.net/processed/${sku_id}/point_cloud.ply`;
                const pointCloudUrl = `https://spyne-prod-3d-model.s3.amazonaws.com/processed/${sku_id}/point_cloud.ply`;
                // const pointCloudUrl = `/point_cloud_vishal_1.ply`;

                const initialCameraPosition = skuData?.config_details?.model_config?.new_perams?.initialCameraPosition;
                const initialCameraDistance = skuData?.config_details?.model_config?.torus_config?.torus_inner_radius;

                const angleDeviation = Math.atan(0.01); // Need this value to be dynamic later

                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

                // Validate tier parameter if provided
                const validTiers = ['low', 'basic', 'medium', 'high', 'ultra-high'];
                const tierOverride = performanceTier && validTiers.includes(performanceTier) ? performanceTier : null;
                
                const deviceProfiler = new DeviceProfiler(tierOverride)
                const optimalSettings = await deviceProfiler.getOptimalSettings()

                // Create viewer instance with custom controls
                const viewer = new Viewer({
                    'selfDrivenMode': true,
                    'cameraUp':[0,0,1],
                    'initialCameraPosition': initialCameraPosition || [-0.5000521540641786, -3.7422714233398438, -0.06316226720809937],
                    'initialCameraLookAt': [0, 0, 0],
                    'sharedMemoryForWorkers': false,
                    "integerBasedSort":true,
                    "ignoreDevicePixelRatio": false,
                    ...optimalSettings
                });

                // Store viewer instance in ref
                viewerRef.current = viewer;

                // Load the scene
                await viewer.addSplatScene(pointCloudUrl, {
                    'splatAlphaRemovalThreshold': 0,
                    'progressiveLoad': true,
                    'showLoadingUI': true,
                    'position': [0, 0, 0],
                    'rotation': [0, 0, 0, 0],
                    'scale': [1, 1, 1]
                });

                viewer.start();
                if (viewer.renderer) {
                    viewer.renderer.setClearColor(0x000000, 0); // Transparent
                }
                

                if (gradient === 'true' && viewer.renderer.domElement && viewer.renderer.domElement.parentElement) {
                        viewer.renderer.domElement.parentElement.style.background =
                            'linear-gradient(to bottom, \
                            #51729c 0%, \
                            #5e85b2 10%, \
                            #79a4d0 30%, \
                            #b9d2de 50%, \
                            #78726f 100% )'; // dynamic ground color will be here later on
                }
                viewer.controls.minDistance = initialCameraDistance * 0.9;
                viewer.controls.maxDistance = initialCameraDistance * 1.1;
                viewer.controls.minPolarAngle = Math.PI / 2 - angleDeviation; // radians
                viewer.controls.maxPolarAngle =  Math.PI /2 + angleDeviation; // radians
                viewer.controls.dampingFactor = 0.06;
                viewer.controls.autoRotateSpeed = 2.0;
                // Set autoRotate to true by default, only disable if explicitly set to 'false'
                viewer.controls.autoRotate = autoRotate !== 'false';
                viewer.controls.enableDamping = true; // Enable damping (inertia)
                viewer.controls.zoomSpeed = 0.5; // Reduce zoom speed for smoother zooming
                viewer.update();

                // Add lighting to the scene for GLB models
                const ambientLight = new THREE.AmbientLight(0xffffff, 1.5); // Bright ambient light
                viewer.threeScene.add(ambientLight);
                lightsRef.current.push(ambientLight);

                const directionalLight1 = new THREE.DirectionalLight(0xffffff, 1.0);
                directionalLight1.position.set(5, 10, 5);
                viewer.threeScene.add(directionalLight1);
                lightsRef.current.push(directionalLight1);

                const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
                directionalLight2.position.set(-5, 5, -5);
                viewer.threeScene.add(directionalLight2);
                lightsRef.current.push(directionalLight2);

                // Load GLB model only if bg=true in query parameters
                if (bg === 'true') {
                    // Clean up existing model before loading new one
                    cleanupGLBModel();
                    
                    // Use custom model if uploaded, otherwise use default S3 model
                    const modelSource = customModelUrl || 'https://spyne-prod-3d-model.s3.amazonaws.com/bg.glb';
                    try {
                        await loadGLBModel(viewer, modelSource);
                    } catch (error) {
                        console.warn('GLB model loading failed, continuing without it:', error.message);
                    }
                }
            } catch (error) {
                const errorMessage = `Error fetching SKU data: ${error.message}`;
                console.error(errorMessage);
                document.body.innerHTML = `<div style="color: white; text-align: center; margin-top: 50px;">${errorMessage}</div>`;
            }
        };

        initViewer();

        // Cleanup function
        return () => {
            // Clean up lights
            if (viewerRef.current?.threeScene && lightsRef.current.length > 0) {
                lightsRef.current.forEach(light => {
                    viewerRef.current.threeScene.remove(light);
                    light.dispose?.();
                });
                lightsRef.current = [];
            }

            // Clean up GLB model using dedicated function
            cleanupGLBModel();
            
            // Clean up viewer
            if (viewerRef.current) {
                viewerRef.current.dispose();
                viewerRef.current = null;
            }
        };
    }, [sku_id, glbUrl, bg, gradient, customModelUrl]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        const validExtensions = ['.glb', '.gltf'];
        const fileName = file.name.toLowerCase();
        const isValidFile = validExtensions.some(ext => fileName.endsWith(ext));

        if (!isValidFile) {
            alert('Please upload only GLB or GLTF files');
            event.target.value = ''; // Reset file input
            return;
        }

        console.log('Loading custom background model:', file.name);
        setCustomModelUrl(file);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };
 
    return (
        <>
            {bg === 'true' && (
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".glb,.gltf"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                    />
                    <button
                        onClick={handleUploadClick}
                        style={{
                            position: 'fixed',
                            top: '20px',
                            right: '20px',
                            padding: '10px 16px',
                            backgroundColor: '#4600F2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#4600F2';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#4600F2';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                        }}
                    >
                        📁 Upload Background
                    </button>
                </>
            )}
        </>
    );
}

export default View3DNew